import { useId } from 'react';
import ComboSelect from '@/components/common/ComboSelect';
import { Input, Select } from '@/components/ui/FormControls';
import FieldHint from '@/components/common/FieldHint';
import {
  JERSEY_DETAIL_KINDS,
  emptyJerseyDetails,
  jerseyVariantToken,
  type JerseyIdentifierContext,
  type JerseySkuReview as Review,
} from '@klothiq/domain';

export default function JerseySkuReview({
  context,
  value,
  onChange,
  error,
  required = true,
}: {
  context: JerseyIdentifierContext;
  value: Review | null;
  onChange: (value: Review) => void;
  error?: string;
  required?: boolean;
}) {
  const id = useId();
  if (!context.enabled || !context.config || !context.configuration_version_id) return null;
  const details = value?.details || context.review?.details || emptyJerseyDetails();
  const legacy =
    value?.legacy_variants ||
    (context.legacy_variants || []).map((variant) => {
      let colour = variant.colour_key || '',
        size = variant.size_key || '';
      try {
        colour ||= jerseyVariantToken(context.config!, 'colour', variant.color || '').key;
      } catch {
        /* Explicit review required. */
      }
      try {
        size ||= jerseyVariantToken(context.config!, 'size', variant.size).key;
      } catch {
        /* Explicit review required. */
      }
      return { variant_id: variant.id, colour_key: colour, size_key: size };
    });
  const confirmed =
    !!value?.confirmed && value.configuration_version_id === context.configuration_version_id;
  const change = (next: typeof details) =>
    onChange({
      details: next,
      legacy_variants: legacy,
      confirmed: false,
      configuration_version_id: context.configuration_version_id!,
    });
  return (
    <details
      className="rounded-xl border border-primary/20 bg-primary/5 p-4"
      open={required || !!error}
    >
      <summary className="min-h-11 cursor-pointer text-sm font-semibold">
        Jersey code and SKU details
      </summary>
      <p className="mb-4 text-xs leading-relaxed text-muted-foreground">
        Review the details used for new identifiers. Your product name and existing codes/SKUs stay
        unchanged. Choose Unspecified when a detail is genuinely unknown. Missing mappings are
        managed in Category setup.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-sm" htmlFor={`${id}-team`}>
          Team
          <ComboSelect
            id={`${id}-team`}
            value={details.team_id}
            clearable={false}
            required={!context.existing_product}
            hasError={!!error && !context.existing_product && !details.team_id}
            options={context.config.teams.map((team) => ({
              value: team.team_id,
              label: context.teams.find((t) => t.id === team.team_id)?.name || 'Configured team',
              sublabel: team.code,
            }))}
            onChange={(v) => change({ ...details, team_id: v || '' })}
            helpLabel="Jersey code team"
          />
        </label>
        <label className="space-y-1 text-sm" htmlFor={`${id}-period-kind`}>
          Period
          <Select
            id={`${id}-period-kind`}
            className="w-full"
            value={details.period.kind}
            helpLabel="Jersey SKU period kind"
            onChange={(e) =>
              change({
                ...details,
                period: { kind: e.target.value as typeof details.period.kind, value: null },
              })
            }
          >
            <option value="unspecified">Unspecified</option>
            <option value="season">Season</option>
            <option value="calendar_year">Calendar year</option>
          </Select>
        </label>
        {details.period.kind !== 'unspecified' && (
          <label className="space-y-1 text-sm" htmlFor={`${id}-period`}>
            {details.period.kind === 'season' ? 'Season' : 'Year'}
            <Input
              id={`${id}-period`}
              value={details.period.value || ''}
              helpLabel={
                details.period.kind === 'season' ? 'Jersey SKU season' : 'Jersey SKU calendar year'
              }
              maxLength={9}
              placeholder={details.period.kind === 'season' ? '2026/27' : '2015'}
              onChange={(e) =>
                change({ ...details, period: { ...details.period, value: e.target.value } })
              }
            />
            <FieldHint>A calendar year remains a calendar year; no season is inferred.</FieldHint>
          </label>
        )}
        {JERSEY_DETAIL_KINDS.map((kind) => (
          <label key={kind} className="space-y-1 text-sm" htmlFor={`${id}-${kind}`}>
            <span className="capitalize">{kind}</span>
            <ComboSelect
              id={`${id}-${kind}`}
              value={details.selections[kind]}
              clearable={false}
              required
              hasError={!!error && !details.selections[kind]}
              options={context
                .config!.tokens[kind].filter((t) => t.active !== false)
                .map((t) => ({ value: t.key, label: t.label, sublabel: t.token }))}
              onChange={(v) =>
                change({ ...details, selections: { ...details.selections, [kind]: v || '' } })
              }
              helpLabel={`Jersey SKU ${kind}`}
            />
          </label>
        ))}
      </div>
      {legacy.length > 0 && (
        <details
          className="mt-4 rounded-xl border border-border p-3"
          open={legacy.some((v) => !v.colour_key || !v.size_key)}
        >
          <summary className="min-h-11 cursor-pointer text-sm font-medium">
            Review existing variant identities ({legacy.length})
          </summary>
          <p className="mb-3 text-xs text-muted-foreground">
            Map existing colours and sizes to prevent duplicate variants. This does not change their
            labels, SKUs or stock.
          </p>
          <div className="space-y-3">
            {legacy.map((mapping, index) => {
              const variant = context.legacy_variants?.find((v) => v.id === mapping.variant_id);
              return (
                <div key={mapping.variant_id} className="space-y-2 border-t border-border pt-3">
                  <p className="break-all text-xs">
                    {variant?.sku} · {variant?.color || 'No colour recorded'} / {variant?.size}
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {(['colour', 'size'] as const).map((kind) => {
                      const field = kind === 'colour' ? 'colour_key' : 'size_key';
                      return (
                        <label
                          key={kind}
                          className="space-y-1 text-xs"
                          htmlFor={`${id}-legacy-${index}-${kind}`}
                        >
                          Existing {kind}
                          <ComboSelect
                            id={`${id}-legacy-${index}-${kind}`}
                            value={mapping[field]}
                            clearable={false}
                            required
                            hasError={!!error && !mapping[field]}
                            helpLabel={`Existing variant ${index + 1} ${kind} mapping`}
                            options={context.config!.tokens[kind].map((entry) => ({
                              value: entry.key,
                              label: `${entry.label}${entry.active === false ? ' (retained)' : ''}`,
                              sublabel: entry.token,
                            }))}
                            onChange={(next) =>
                              onChange({
                                details,
                                confirmed: false,
                                configuration_version_id: context.configuration_version_id!,
                                legacy_variants: legacy.map((m, n) =>
                                  n === index ? { ...m, [field]: next || '' } : m,
                                ),
                              })
                            }
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </details>
      )}
      {required && (
        <label className="mt-4 flex min-h-11 items-start gap-3 rounded-lg border border-border p-3 text-sm">
          <Input
            type="checkbox"
            checked={confirmed}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : undefined}
            onChange={(e) =>
              onChange({
                details,
                legacy_variants: legacy,
                confirmed: e.target.checked,
                configuration_version_id: context.configuration_version_id!,
              })
            }
            helpLabel="Confirm jersey SKU details review"
          />
          I reviewed these details for the new product code and SKUs.
        </label>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-2 text-sm text-destructive">
          {error}
        </p>
      )}
    </details>
  );
}
